import React, {
  useState,
  useCallback
} from 'react';
import { string } from 'prop-types';
import { CardMedia } from '@one-thd/sui-atomic-components';

/**
 * HoverableCardMedia component that displays a primary image and switches to a secondary image on hover.
 *
 * @param {string[]} primaryURL - URL of the primary image to display.
 * @param {string} secondaryURL - URL of the secondary image to display on hover.
 * @param {string} alt - Alt text for the image.
 * @param {string} aspect - Aspect ratio for the CardMedia component (e.g., 'square', 'wide').
 *
 */
export const HoverableCardMedia = ({ primaryURL, secondaryURL, alt, aspect }) => {
  const [currentImage, setCurrentImage] = useState(primaryURL);

  const handleMouseEnter = useCallback(() => {
    if (secondaryURL) {
      setCurrentImage(secondaryURL);
    }
  }, [secondaryURL]);

  const handleMouseLeave = useCallback(() => {
    setCurrentImage(primaryURL);
  }, [primaryURL]);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={
        [
          'transition-all duration-300 ease-in-out sui-overflow-hidden sui-flex sui-items-center sui-justify-center',
          'min-[1440px]:sui-w-[150px] min-[1440px]:sui-h-[150px]',
          'min-[1600px]:sui-w-[200px] min-[1600px]:sui-h-[200px]'
        ].join(' ')
      }
    >
      <CardMedia
        src={currentImage}
        alt={alt}
        aspect={aspect}
        className={
          [
            'min-[1440px]:sui-w-[150px] min-[1440px]:sui-h-[150px]',
            'min-[1600px]:sui-w-[200px] min-[1600px]:sui-h-[200px]',
            'sui-object-cover sui-transform sui-scale-150',
          ].join(' ')
        }
      />
    </div>
  );
};

HoverableCardMedia.propTypes = {
  primaryURL: string.isRequired,
  secondaryURL: string,
  alt: string.isRequired,
  aspect: string
};

HoverableCardMedia.defaultProps = {
  secondaryURL: null,
  aspect: 'wide'
};